import { Instance, SnapshotIn, types } from "mobx-state-tree";
import { withSetPropAction } from "mobx-stores/_extension/with-set-props";
import { Base, Catalogue } from "../base";
import { Customer } from "../customers/customer";
import { Order } from "../orders/order";
import { OrderItem } from "../orders/order-item";
import {
  AcceptanceWorkOrderPOProgressModel,
  CutWorkOrderPOProgressModel,
  FoilWorkOrderPOProgressModel,
  MatteWorkOrderPOProgressModel,
  PackageProgressModel,
  PaperOutputWorkOrderPOProgressModel,
  PasteWorkOrderPOProgressModel,
  PrintWorkOrderPOProgressModel,
  ShapeWorkOrderPOProgressModel,
} from "./progress/progress-models";

export const WorkOrderPo = types
  .compose(
    Base,
    types.model({
      orderNumber: types.maybeNull(types.number),
      date: types.maybeNull(types.string),
      woNumber: types.maybeNull(types.string),
      woBatchNumber: types.maybeNull(types.string),
      productionAmount: types.maybeNull(types.number),
      note: types.maybeNull(types.string),
      customerId: types.maybeNull(types.string),
      customer: types.maybeNull(Customer),
      purchaseOrderId: types.maybeNull(types.string),
      purchaseOrder: types.maybeNull(Order),
      purchaseOrderItemId: types.maybeNull(types.string),
      purchaseOrderItem: types.maybeNull(OrderItem),
      statusId: types.maybeNull(types.string),
      status: types.maybeNull(Catalogue),
      isCompensating: types.maybeNull(types.boolean),
      poItemGSM: types.maybeNull(types.number),
      poItemFirstFullPaperSize: types.maybeNull(types.number),
      poItemFirstFullPaperLength: types.maybeNull(types.number),
      poItemFirstFullPaperAmount: types.maybeNull(types.number),
      poItemSecondFullPaperSize: types.maybeNull(types.number),
      poItemSecondFullPaperLength: types.maybeNull(types.number),
      poItemSecondFullPaperAmount: types.maybeNull(types.number),
      poItemPrintPaperSize: types.maybeNull(types.number),
      poItemPrintPaperLength: types.maybeNull(types.number),
      poItemFirstPrintPaperAmount: types.maybeNull(types.number),
      poItemSecondPrintPaperSize: types.maybeNull(types.number),
      poItemSecondPrintPaperLength: types.maybeNull(types.number),
      poItemSecondPrintPaperAmount: types.maybeNull(types.number),
      poItemProductionAmount: types.maybeNull(types.number),
      completedAmount: types.maybeNull(types.number),
      lossRate: types.maybeNull(types.number),
      totalPaperWeight: types.maybeNull(types.number),
      poItemNote: types.maybeNull(types.string),
      paperOutputProgress: types.maybeNull(PaperOutputWorkOrderPOProgressModel),
      printProgress: types.maybeNull(PrintWorkOrderPOProgressModel),
      matteProgress: types.maybeNull(MatteWorkOrderPOProgressModel),
      acceptanceProgress: types.maybeNull(AcceptanceWorkOrderPOProgressModel),
      foilProgress: types.maybeNull(FoilWorkOrderPOProgressModel),
      shapeProgress: types.maybeNull(ShapeWorkOrderPOProgressModel),
      pasteProgress: types.maybeNull(PasteWorkOrderPOProgressModel),
      cutProgress: types.maybeNull(CutWorkOrderPOProgressModel),
      packageProgress: types.maybeNull(PackageProgressModel),
    }),
  )
  .views((self) => ({
    get customerDisplay() {
      return (
        self?.purchaseOrder?.customer ??
        self?.purchaseOrderItem?.product?.customer ??
        self?.customer
      );
    },
    get product() {
      return self.purchaseOrderItem?.product;
    },
  }))
  .views((self) => ({
    get paperOutputStandardDisplay() {
      return {
        standard:
          self.purchaseOrderItem?.product?.process?.paperOutputStandard ?? "",
        note: self.purchaseOrderItem?.product?.process?.note ?? "",
        paperType: self.purchaseOrderItem?.product?.material?.paperType ?? "",
        gsmStandard:
          self.purchaseOrderItem?.product?.material?.gsmStandard ?? "",
        paperBrand: self.purchaseOrderItem?.product?.material?.paperBrand ?? "",
      };
    },

    get printStandardDisplay() {
      return {
        standard: self.purchaseOrderItem?.product?.process?.printStandard ?? "",
        note: self.purchaseOrderItem?.product?.process?.printNote ?? "",
      };
    },
    get matteStandardDisplay() {
      return {
        standard:
          self.purchaseOrderItem?.product?.process?.coatingStandard ?? "",
        note: "",
      };
    },
    get acceptanceStandardDisplay() {
      return {
        standard:
          self.purchaseOrderItem?.product?.process?.acceptanceStandard ?? "",
        note: "",
      };
    },
    get foilStandardDisplay() {
      return {
        standard:
          self.purchaseOrderItem?.product?.process?.foilProcessingStandard ??
          "",
        note: self.purchaseOrderItem?.product?.process?.foilNote ?? "",
      };
    },
    get shapeStandardDisplay() {
      return {
        standard:
          self.purchaseOrderItem?.product?.process?.shapeFormingStandard ?? "",
        note: self.purchaseOrderItem?.product?.process?.shapeNote ?? "",
      };
    },
    get pasteStandardDisplay() {
      return {
        standard: self.purchaseOrderItem?.product?.process?.pasteStandard ?? "",
        note: self.purchaseOrderItem?.product?.process?.pasteNote ?? "",
      };
    },
    get cutStandardDisplay() {
      return {
        standard: self.purchaseOrderItem?.product?.process?.cutStandard ?? "",
        note: self.purchaseOrderItem?.product?.process?.cutNote ?? "",
      };
    },
    get packageStandardDisplay() {
      return {
        standard:
          self.purchaseOrderItem?.product?.process?.packageStandard ?? "",
        note: self.purchaseOrderItem?.product?.process?.packageNote ?? "",
        packageProcess: self.purchaseOrderItem?.product?.process?.package ?? "",
      };
    },
  }))
  .actions(withSetPropAction);

export interface IWorkOrderPo extends Instance<typeof WorkOrderPo> {}
export interface IWorkOrderReadOnlyInfo {
  woPoLabelReadOnlyInfo?: string;
  dealingNameReadOnlyInfo?: string;
  productCodeReadOnlyInfo?: string;
  productNameReadOnlyInfo?: string;
  orderDateReadOnlyInfo?: string;
  deliveryDateReadOnlyInfo?: string;
  orderAmountReadOnlyInfo?: string;
  orderAmountUpperReadOnlyInfo?: string;
  orderAmountLowerReadOnlyInfo?: string;
  productBarcodeReadOnlyInfo?: string;
  paperProductionInfoLabel?: string;
  paperTypeReadOnlyInfo?: string;
  gsmReadOnlyInfo?: string;
  paperBrandReadOnlyInfo?: string;
  firstFullPaperSizeReadOnlyInfo?: string;
  firstFullPaperLengthReadOnlyInfo?: string;
  secondFullPaperSizeReadOnlyInfo?: string;
  secondFullPaperLengthReadOnlyInfo?: string;

  firstPrintPaperSizeReadOnlyInfo?: string;
  firstPrintPaperLengthReadOnlyInfo?: string;
  secondPrintPaperSizeReadOnlyInfo?: string;
  secondPrintPaperLengthReadOnlyInfo?: string;
}
export interface IWorkOrderPoProperties extends SnapshotIn<
  typeof WorkOrderPo
> {}

export interface IAddEditWorkOrderPo
  extends
    IWorkOrderReadOnlyInfo,
    Omit<
      IWorkOrderPoProperties,
      | "customer"
      | "purchaseOrder"
      | "purchaseOrderItem"
      | "status"
      | keyof typeof Base
    > {}
export interface IAddWorkOrderPo extends IAddEditWorkOrderPo {}

export interface IUpdateWorkOrderPo extends IAddEditWorkOrderPo {}

export interface IUpdateWorkOrderPoStatus extends Pick<
  IWorkOrderPoProperties,
  "statusId" | "isCompensating"
> {}
